'use client';

import { EntityType } from '@/types/notifications';
import { Calendar, PartyPopper, Tag, Users } from 'lucide-react';
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TakeActionButtonsProps {
  onSayCongrats?: (e: React.MouseEvent) => void;
  entityType?: EntityType;
  isFormDirty?: boolean;
  createPollPost?: () => void;
}

export default function TakeActionButtons({
  onSayCongrats,
  entityType,
  isFormDirty,
  createPollPost,
}: TakeActionButtonsProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [isPoll, setIsPoll] = useState(false);

  const disabledForMarketplace =
    entityType === 'item' ||
    entityType === 'provider' ||
    entityType === 'request';

  function handleNavigate(e: React.MouseEvent, url: string) {
    e.preventDefault();
    e.stopPropagation();

    if (isFormDirty) {
      setPendingUrl(url);
      setConfirmOpen(true);
      return;
    }

    window.location.href = url;
  }

  function handleConfirmDiscard() {
    if (pendingUrl) {
      window.location.href = pendingUrl;
    }
  }

  function handleCancelDiscard() {
    setConfirmOpen(false);
    setPendingUrl(null);
  }

  return (
    <>
      <AlertDialog open={confirmOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDiscard}>
              Keep editing
            </AlertDialogCancel>

            <AlertDialogAction
              onClick={handleConfirmDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------------- UI ---------------- */}
      <div className="w-[25%] flex flex-col gap-4">
        <h2 className="text-center text-lg font-bold pl-4">Take Action</h2>

        <div className="grid grid-cols-1 gap-4 border-l pl-4 border-[#948780] ml-1">
          {/* Create Event */}
          <button
            onClick={(e) =>
              handleNavigate(e, '/happenings?openCreateEvent=true')
            }
            className="bg-[#cdc4c1] rounded-4xl flex flex-col items-center py-4 shadow-sm cursor-pointer"
          >
            <Calendar className="w-6 h-6 mb-2" />
            <span className="text-sm font-medium text-center">
              Create an Event
            </span>
          </button>

          {/* Marketplace */}
          {disabledForMarketplace ? (
            <button
              disabled
              className="bg-[#cdc4c1] disabled:opacity-50 rounded-4xl flex flex-col items-center py-4 shadow-sm cursor-not-allowed"
            >
              <Tag className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium text-center">
                Sell or Give Away
              </span>
            </button>
          ) : (
            <button
              onClick={(e) =>
                handleNavigate(
                  e,
                  '/marketplace/items?openCreateListing=true'
                )
              }
              className="bg-[#cdc4c1] rounded-4xl flex flex-col items-center py-4 shadow-sm cursor-pointer"
            >
              <Tag className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium text-center">
                Sell or Give Away
              </span>
            </button>
          )}

          {/* Poll Members */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (entityType === 'post') {
                 createPollPost?.();
              } else {
                 handleNavigate(e, '/forums?openCreatePoll=true');
              }
              setIsPoll(true);
            }}
            className="bg-[#cdc4c1] disabled:opacity-50 rounded-4xl flex flex-col items-center py-4 shadow-sm cursor-pointer"
          >
            <Users className="w-6 h-6 mb-2" />
            <span className="text-sm font-medium text-center">
              Poll Members
            </span>
          </button>

          {/* Say Congrats */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (entityType === 'post' && !isPoll) {
                onSayCongrats?.(e);
              } else {
                handleNavigate(
                  e,
                  '/forums?openPostCongrats=true&template=congrats'
                );
              }
            }}
            className="bg-[#cdc4c1] rounded-4xl flex flex-col items-center py-4 shadow-sm cursor-pointer"
          >
            <PartyPopper className="w-6 h-6 mb-2" />
            <span className="text-sm font-medium text-center">
              Say Congrats
            </span>
          </button>
        </div>
      </div>
    </>
  );
}